import { useCallback, useLayoutEffect, useRef } from 'react';

const SWITCH_ATTRIBUTE = 'data-skin-switching';

function canFadeContent() {
  const media = query => Boolean(window.matchMedia?.(query).matches);
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return document.visibilityState !== 'hidden'
    && !media('(prefers-reduced-motion: reduce)')
    && !media('(max-width: 768px)')
    && !media('(pointer: coarse)')
    && !connection?.saveData
    && !(typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4)
    && !(typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4);
}

/** Switch immediately; optional feedback touches only the content's opacity. */
export default function useSkinChangeFeedback(skin) {
  const active = useRef(null);
  const currentSkin = useRef(skin);
  const committedSkin = useRef(skin);
  currentSkin.current = skin;

  const release = useCallback(run => {
    if (!run || active.current !== run) return;
    window.cancelAnimationFrame(run.frame);
    window.clearTimeout(run.timer);
    if (run.animation) {
      run.animation.onfinish = null;
      run.animation.oncancel = null;
      run.animation.cancel();
    }
    active.current = null;
    document.documentElement.removeAttribute(SWITCH_ATTRIBUTE);
  }, []);

  const begin = useCallback(() => {
    release(active.current);
    const run = { animation: null, frame: 0, timer: 0, started: false };
    active.current = run;
    document.documentElement.setAttribute(SWITCH_ATTRIBUTE, '');
    // A superseded React update or background tab must never leave hover disabled.
    run.timer = window.setTimeout(() => release(run), 240);
    return run;
  }, [release]);

  const prepareSkinChange = useCallback(nextSkin => {
    if (nextSkin === currentSkin.current) { release(active.current); return; }
    begin();
  }, [begin, release]);

  useLayoutEffect(() => {
    if (committedSkin.current === skin) return;
    committedSkin.current = skin;
    // Storage events have no prepare phase; replace any already-running feedback.
    const run = active.current && !active.current.started ? active.current : begin();
    run.started = true;
    const content = document.querySelector('.main-content');
    if (canFadeContent() && typeof content?.animate === 'function') {
      try {
        run.animation = content.animate([{ opacity: .85 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
        run.animation.onfinish = () => release(run);
        run.animation.oncancel = () => release(run);
        return;
      } catch { /* A failed optional animation must not interrupt a theme change. */ }
    }
    // Let the new styles paint without CSS transitions, then restore normal hover.
    run.frame = window.requestAnimationFrame(() => {
      run.frame = window.requestAnimationFrame(() => release(run));
    });
  }, [skin, begin, release]);

  useLayoutEffect(() => () => release(active.current), [release]);
  return prepareSkinChange;
}
